/**
 * ONE place that says which QC phantom an MG series shows.
 *
 * Mammography QC sites image three phantoms in one weekly study: the D-shaped
 * SDNR phantom (~45 mm; mAs / signal / SDNR are measured on it), a flat-field
 * phantom imaged paddle-up (its DICOM thickness reads the paddle holder
 * height, ~60 mm and above; artifact review), and the ACR / RMI phantom
 * (~42 mm; fibres / specks / masses). The analyzer classifies each image by
 * its pixels (`image_type`), but that classifier occasionally files an ACR or
 * flat-field exposure as `sdnr`, so the identification also reads the
 * exposure's ProtocolName and thickness. Consumers (evaluation, baselines,
 * trends on server and client, reports, the grouped test layout) call
 * `classifyMgSeries` and never re-derive the rule; if the analyzer's
 * classifier becomes reliable this collapses to reading `image_type` and no
 * consumer changes. Inputs are analyzer measurements only -- never a
 * tenant-specific convention such as the PatientName a site types in.
 *
 * Shared by the server and the client (imported as `common/mgPhantom`).
 */
import { SeriesGroup } from "./aqsTypes";

export type MgPhantomKind = "sdnr" | "flat_field" | "acr" | "other";

export const MG_IMAGE_TYPE = {
  sdnr: "MG_Type.sdnr",
  uniform: "MG_Type.uniform",
  acr: "MG_Type.acr_small",
};

export const FOR_PROCESSING = "FOR PROCESSING";
export const FOR_PRESENTATION = "FOR PRESENTATION";

/** an "SDNR" image whose thickness reads this or more is the paddle-up flat-field phantom */
export const FLAT_FIELD_MIN_THICKNESS_MM = 60;

/**
 * The minimal series shape the identification needs; both the generated
 * `MG_TestResultSeries` and the client's untyped trend data satisfy it.
 */
export interface MgSeriesLike {
  seriesUID?: unknown;
  parameters?: {
    ProtocolName?: unknown;
    BodyPartThickness?: unknown;
    PresentationIntentType?: unknown;
    SeriesDescription?: unknown;
  };
  images?: Array<{ image_type?: unknown } | null | undefined>;
}

export function hasImageType(series: MgSeriesLike, imageType: string): boolean {
  const images = series.images || [];
  for (const image of images) {
    if (image && image.image_type === imageType) {
      return true;
    }
  }
  return false;
}

/**
 * Rules, in priority order:
 *  1. ProtocolName names the ACR phantom, or the analyzer saw the ACR phantom  -> acr
 *  2. the analyzer saw a uniform (flat-field) image                            -> flat_field
 *  3. the analyzer saw the SDNR phantom: thickness >= 60 mm -> flat_field (paddle up), else -> sdnr
 *     (a missing / non-numeric thickness keeps the series as sdnr)
 *  4. anything else                                                             -> other
 * Rule 3's `sdnr` set is exactly the set the SDNR evaluation, baseline and
 * trend code accepted before this module existed (verified over every MG
 * series in production, 2026-09).
 */
export function classifyMgSeries(series: MgSeriesLike): MgPhantomKind {
  const parameters = series.parameters || {};
  const protocol = String(parameters.ProtocolName || "");
  if (/acr/i.test(protocol) || hasImageType(series, MG_IMAGE_TYPE.acr)) {
    return "acr";
  }
  if (hasImageType(series, MG_IMAGE_TYPE.uniform)) {
    return "flat_field";
  }
  if (hasImageType(series, MG_IMAGE_TYPE.sdnr)) {
    const thickness = parameters.BodyPartThickness;
    if (
      typeof thickness === "number" &&
      thickness >= FLAT_FIELD_MIN_THICKNESS_MM
    ) {
      return "flat_field";
    }
    return "sdnr";
  }
  return "other";
}

export function isForProcessing(series: MgSeriesLike): boolean {
  return (series.parameters || {}).PresentationIntentType === FOR_PROCESSING;
}

export function isForPresentation(series: MgSeriesLike): boolean {
  return (series.parameters || {}).PresentationIntentType === FOR_PRESENTATION;
}

/**
 * The series that mAs, signal and SDNR are measured on, baselined from and
 * trended: the FOR PRESENTATION exposure of the D-shaped SDNR phantom.
 */
export function isSdnrMeasurementSeries(series: MgSeriesLike): boolean {
  return classifyMgSeries(series) === "sdnr" && isForPresentation(series);
}

/** display order of the phantoms in the grouped layout */
export const MG_PHANTOM_ORDER: MgPhantomKind[] = [
  "sdnr",
  "flat_field",
  "acr",
  "other",
];

export const MG_PHANTOM_TITLES: { [kind in MgPhantomKind]: string } = {
  sdnr: "SDNR Test (D-shaped phantom)",
  flat_field: "Artifact Evaluation (flat-field phantom)",
  acr: "Phantom Image Quality (ACR / RMI phantom)",
  other: "Other images",
};

/** how a series ends up in each group, in words (rules above) */
export const MG_PHANTOM_IDENTIFIED_BY: { [kind in MgPhantomKind]: string } = {
  sdnr:
    "analyzer found the SDNR phantom; thickness under 60 mm; protocol is not ACR",
  flat_field:
    "analyzer found a uniform image, or the SDNR phantom at 60 mm or more (paddle up)",
  acr: "protocol names the ACR phantom, or the analyzer found the ACR phantom",
  other: "not recognised as one of the three QC phantoms",
};

export interface MgSeriesGroupMember<S extends MgSeriesLike = MgSeriesLike> {
  /** position in rawOutput.series -- the "Series N" of the series-list layout */
  index: number;
  series: S;
}

export interface MgSeriesGroup<S extends MgSeriesLike = MgSeriesLike> {
  kind: MgPhantomKind;
  title: string;
  members: MgSeriesGroupMember<S>[];
}

/**
 * Groups a test's series by phantom, in `MG_PHANTOM_ORDER`; kinds with no
 * series are omitted; inside a group the rawOutput order is kept (the
 * analyzer orders by SeriesInstanceUID, so FOR PROCESSING precedes FOR
 * PRESENTATION for a pair).
 */
export function groupMgSeries<S extends MgSeriesLike>(
  series: Array<S | null | undefined> | null | undefined
): MgSeriesGroup<S>[] {
  const byKind: { [kind in MgPhantomKind]: MgSeriesGroupMember<S>[] } = {
    sdnr: [],
    flat_field: [],
    acr: [],
    other: [],
  };
  (series || []).forEach((s, index) => {
    if (s) {
      byKind[classifyMgSeries(s)].push({ index, series: s });
    }
  });
  return MG_PHANTOM_ORDER.filter(
    (kind) => byKind[kind].length > 0
  ).map((kind) => ({
    kind,
    title: MG_PHANTOM_TITLES[kind],
    members: byKind[kind],
  }));
}

/** the persisted shape (`evaluationItems.seriesGroups`) the image viewer groups galleries by */
export function toSeriesGroups(groups: MgSeriesGroup[]): SeriesGroup[] {
  return groups.map((group) => ({
    key: group.kind,
    title: group.title,
    seriesUIDs: group.members.map((m) => String(m.series.seriesUID || "")),
  }));
}
